import React, { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, AlertCircle, CheckCircle, Loader2, Calendar, CalendarIcon, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Calendar as CalendarComponent } from './ui/calendar';
import { cn } from '@/lib/utils';
import { smartCompress, CompressionOptions, CompressionResult, formatFileSize, formatCompressionRatio } from '@/lib/imageUtils';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { useAuth } from '@/context/AuthContext';
import UploadProgress from './UploadProgress';
import { format } from 'date-fns';
import { usePostHog } from 'posthog-js/react';

interface PhotoUploadProps {
    onUploadSuccess?: (file: File, compressionResult?: CompressionResult) => void;
    onUploadError?: (error: string) => void;
    maxFileSize?: number; // in bytes
    allowedTypes?: string[];
    disabled?: boolean;
    compressionOptions?: CompressionOptions;
    enableCompression?: boolean;
    enableAutoUpload?: boolean; // Auto upload after file selection
    uploadEndpoint?: string;
    additionalUploadData?: Record<string, any>;
}

interface ValidationError {
    type: 'size' | 'format' | 'general';
    message: string;
}

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function PhotoUpload({
    onUploadSuccess,
    onUploadError,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    allowedTypes = DEFAULT_ALLOWED_TYPES,
    disabled = false,
    compressionOptions = {},
    enableCompression = true,
    enableAutoUpload = false,
    uploadEndpoint = '/api/photos', // Used by child components via props
    additionalUploadData = {}
}: PhotoUploadProps) {
    const { user, session } = useAuth();
    const posthog = usePostHog();
    
    // Suppress unused variable warning for future use
    void uploadEndpoint;
    const [validationError, setValidationError] = useState<ValidationError | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isCompressing, setIsCompressing] = useState(false);
    const [compressionResult, setCompressionResult] = useState<CompressionResult | null>(null);
    const [photoDate, setPhotoDate] = useState<Date>(() => {
        // Default to today's date
        return new Date();
    });
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    // Check if user is authenticated
    const isAuthenticated = user && session;

    // Upload hook with progress tracking
    const { uploadState, uploadPhoto, resetUpload, retryUpload } = usePhotoUpload({
        onSuccess: (_response) => {
            setIsSuccess(true);

            // Track photo upload event in PostHog
            posthog.capture('photo_uploaded');

            onUploadSuccess?.(selectedFile!, compressionResult || undefined);
        },
        onError: (error) => {
            onUploadError?.(error);
        }
    });

    // Validate file
    const validateFile = useCallback((file: File): ValidationError | null => {
        // Check file type
        if (!allowedTypes.includes(file.type)) {
            return {
                type: 'format',
                message: `Invalid file format. Please select ${allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')} files only.`
            };
        }

        // Check file size
        if (file.size > maxFileSize) {
            return {
                type: 'size',
                message: `File size too large. Maximum allowed size is ${formatFileSize(maxFileSize)}.`
            };
        }

        return null;
    }, [allowedTypes, maxFileSize]);

    // Handle file selection with compression and optional auto-upload
    const handleFileSelect = useCallback(async (file: File) => {
        setValidationError(null);
        setIsSuccess(false);
        setCompressionResult(null);
        resetUpload();

        const error = validateFile(file);
        if (error) {
            setValidationError(error);
            setSelectedFile(null);
            onUploadError?.(error.message);
            return;
        }

        setSelectedFile(file);

        let finalFile = file;
        let compressionData: CompressionResult | undefined;

        // Apply compression if enabled
        if (enableCompression) {
            setIsCompressing(true);
            try {
                const result = await smartCompress(file, compressionOptions);
                setCompressionResult(result);
                finalFile = result.file;
                compressionData = result;
            } catch (compressionError) {
                const errorMessage = compressionError instanceof Error
                    ? compressionError.message
                    : 'Image compression failed';
                setValidationError({
                    type: 'general',
                    message: errorMessage
                });
                onUploadError?.(errorMessage);
                setIsCompressing(false);
                return;
            } finally {
                setIsCompressing(false);
            }
        }

        setIsSuccess(true);

        // Auto-upload if enabled
        if (enableAutoUpload) {
            try {
                // Include photo date in upload data (convert to ISO string)
                const uploadData = {
                    ...additionalUploadData,
                    photoDate: photoDate.toISOString().split('T')[0]
                };
                await uploadPhoto(finalFile, uploadData);
            } catch (uploadError) {
                // Error is handled by the upload hook
            }
        } else {
            onUploadSuccess?.(finalFile, compressionData);
        }
    }, [validateFile, onUploadSuccess, onUploadError, enableCompression, compressionOptions, enableAutoUpload, uploadPhoto, additionalUploadData, resetUpload]);

    // Handle file input change
    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    // Handle camera capture
    const handleCameraCapture = () => {
        if (!isAuthenticated) {
            setValidationError({
                type: 'general',
                message: 'Please sign in to upload photos.'
            });
            onUploadError?.('Authentication required to upload photos.');
            return;
        }
        if (!disabled) {
            cameraInputRef.current?.click();
        }
    };

    // Handle file selection
    const handleFileSelection = () => {
        if (!isAuthenticated) {
            setValidationError({
                type: 'general',
                message: 'Please sign in to upload photos.'
            });
            onUploadError?.('Authentication required to upload photos.');
            return;
        }
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    // Clear selection
    const handleClear = () => {
        setSelectedFile(null);
        setValidationError(null);
        setIsSuccess(false);
        setCompressionResult(null);
        setIsCompressing(false);
        resetUpload();
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (cameraInputRef.current) {
            cameraInputRef.current.value = '';
        }
    };

    // Manual upload function
    const handleManualUpload = async () => {
        if (!selectedFile) return;

        const fileToUpload = compressionResult?.file || selectedFile;
        try {
            // Include photo date in upload data (convert to ISO string)
            const uploadData = {
                ...additionalUploadData,
                photoDate: photoDate.toISOString().split('T')[0]
            };
            await uploadPhoto(fileToUpload, uploadData);
        } catch (uploadError) {
            // Error is handled by the upload hook
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            {/* Hidden File Inputs */}
            <input
                ref={fileInputRef}
                type="file"
                accept={allowedTypes.join(',')}
                onChange={handleFileInputChange}
                className="hidden"
                disabled={disabled}
            />
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileInputChange}
                className="hidden"
                disabled={disabled}
            />

            {/* Selected File Display */}
            {selectedFile && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    whileHover={{ y: -2 }}
                >
                    <Card className={cn(
                        "bg-card border-border transition-all duration-200",
                        {
                            "border-destructive/50 bg-destructive/5": validationError,
                            "border-green-500/50 bg-green-500/5": isSuccess,
                        }
                    )}>
                        <CardContent className="p-6">
                            <div className="flex flex-col items-center space-y-3">
                                {isCompressing ? (
                                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                ) : isSuccess ? (
                                    <CheckCircle className="w-8 h-8 text-green-500" />
                                ) : validationError ? (
                                    <AlertCircle className="w-8 h-8 text-destructive" />
                                ) : (
                                    <Upload className="w-8 h-8 text-muted-foreground" />
                                )}

                                <div className="text-center">
                                    <p className="text-sm font-medium">
                                        {selectedFile.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {formatFileSize(selectedFile.size)}
                                    </p>
                                </div>
                            </div>

                            {/* Clear Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClear}
                                className="absolute top-2 right-2 h-8 w-8 p-0 hover:bg-muted"
                                disabled={disabled}
                                type="button"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {/* Photo Date Picker */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="space-y-2"
            >
                <Label className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Photo Date
                </Label>
                <DropdownMenu open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="outline"
                            className={cn(
                                "w-full justify-between text-left font-normal",
                                !photoDate && "text-muted-foreground"
                            )}
                            disabled={disabled}
                        >
                            <div className="flex items-center">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {photoDate ? format(photoDate, 'PPP') : <span>Pick a date</span>}
                            </div>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-auto p-0" align="start">
                        <CalendarComponent
                            mode="single"
                            selected={photoDate}
                            onSelect={(date) => {
                                if (date) {
                                    setPhotoDate(date);
                                    setDatePickerOpen(false);
                                }
                            }}
                            disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                        />
                    </DropdownMenuContent>
                </DropdownMenu>
                <p className="text-xs text-muted-foreground">
                    Select the date this photo was taken (defaults to today)
                </p>
            </motion.div>

            {/* Upload Buttons - Premium Design */}
            <motion.div 
                className="grid grid-cols-2 gap-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
            >
                <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                    <Button
                        variant="outline"
                        onClick={handleCameraCapture}
                        disabled={disabled}
                        className="w-full h-12 font-medium"
                        type="button"
                    >
                        <Camera className="w-5 h-5 mr-2" />
                        Take Photo
                    </Button>
                </motion.div>

                <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                    <Button
                        variant="outline"
                        onClick={handleFileSelection}
                        disabled={disabled}
                        className="w-full h-12 font-medium"
                        type="button"
                    >
                        <Upload className="w-5 h-5 mr-2" />
                        Choose File
                    </Button>
                </motion.div>
            </motion.div>

            {/* File Requirements */}
            {!selectedFile && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="text-center text-xs text-muted-foreground space-y-1"
                >
                    <p>Supported formats: {allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')}</p>
                    <p>Maximum size: {formatFileSize(maxFileSize)}</p>
                </motion.div>
            )}

            {/* Error Message */}
            {validationError && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                >
                    <Card className="bg-destructive/5 border-destructive/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                                <p className="text-sm text-destructive">{validationError.message}</p>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {/* Compression Progress */}
            {isCompressing && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                >
                    <Card className="bg-primary/5 border-primary/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                                <p className="text-sm text-primary">
                                    Compressing image for optimal upload...
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {/* Upload Progress */}
            <UploadProgress
                uploadState={uploadState}
                onRetry={retryUpload}
                onCancel={resetUpload}
            />

            {/* Success Message with Compression Info */}
            {isSuccess && selectedFile && !uploadState.isUploading && !uploadState.error && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                >
                    <Card className="bg-green-500/5 border-green-500/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <p className="text-sm text-green-700">
                                    {enableAutoUpload ? 'Uploading...' : 'Photo selected successfully! Ready to upload.'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Compression Results */}
                    {compressionResult && compressionResult.compressionRatio > 0 && (
                        <Card className="bg-muted/30">
                            <CardContent className="p-4">
                                <div className="text-xs text-muted-foreground space-y-2">
                                    <div className="flex justify-between">
                                        <span>Original size:</span>
                                        <span className="font-medium">{formatFileSize(compressionResult.originalSize)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Compressed size:</span>
                                        <span className="font-medium">{formatFileSize(compressionResult.compressedSize)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Space saved:</span>
                                        <span className="text-green-600 font-medium">
                                            {formatCompressionRatio(compressionResult.compressionRatio)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Dimensions:</span>
                                        <span className="font-medium">{compressionResult.dimensions.width} × {compressionResult.dimensions.height}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Manual Upload Button */}
                    {!enableAutoUpload && !uploadState.progress && (
                        <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                onClick={handleManualUpload}
                                disabled={disabled || uploadState.isUploading}
                                className="w-full h-12 font-medium"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Photo
                            </Button>
                        </motion.div>
                    )}
                </motion.div>
            )}
        </motion.div>
    );
}